import styled from "styled-components";
import React from "react";
import urls from "../../urls";
import { Col, Row } from "antd";
import { device } from "../../utils/breakpoints";
import { colors, styles } from "../../utils/theme";
import ReactMarkdown from "react-markdown";
import useIsMobile from "../hooks/useIsMobile";
import { PrimaryLinkButton } from "../buttons/LinkButtons";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import RecipeItem from "../elements/RecipeItem";
import { BackgroundElement } from "../../styles/SharedStyles";
import BannerTitle from "../core/BannerTitle";
import { useInView } from "react-intersection-observer";
import BlogPostItem from "../elements/BlogPostItem";

export default function StoryBanner({
  data,
  dark,
  recipesData = null,
  blogPostsData = null,
  hideAnimation = true,
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { ref, inView } = useInView({
    rootMargin: "-50% 0% -50% 0%",
    triggerOnce: true,
  });

  return (
    <Wrapper dark={dark} ref={ref}>
      {!!recipesData && (
        <>
          <RecipesWrapper>
            <HerbsAndSpicesBgElement
              src={"/assets/img/styling/herbs-and-spices-4.webp"}
              alt={""}
              initial={{
                scale: hideAnimation ? 1 : 2,
                x: hideAnimation ? 0 : "100%",
                rotate: hideAnimation ? 15 : 75,
              }}
              animate={{
                scale: hideAnimation ? 1 : inView ? 1 : 2,
                x: hideAnimation ? 0 : inView ? 0 : "100%",
                rotate: hideAnimation ? 15 : inView ? 15 : 75,
              }}
              transition={{
                ease: "easeIn",
                duration: 0.3,
              }}
            />
            <HerbBgElement src={"/assets/img/styling/herb-4.webp"} alt={""} />
            <StyledContainer>
              <RecipesTitleContainer>
                <img
                  src={"/assets/img/icons/knives-icon-light.webp"}
                  alt={""}
                />
                <BannerTitle dark={dark}>
                  {t("need_inspiration")} <br /> {t("try_our_recipes")}
                </BannerTitle>
              </RecipesTitleContainer>
              <Row gutter={[16, 16]}>
                {recipesData.items.map((item, index) => (
                  <Col lg={12} key={index}>
                    <RecipeItem item={item} />
                  </Col>
                ))}
              </Row>
              <PrimaryLinkButton
                style={{
                  width: "fit-content",
                  minWidth: 200,
                  marginInline: "auto",
                }}
                link={urls.recipes}
                text={t("view_all_recipes")}
              />
            </StyledContainer>
          </RecipesWrapper>
        </>
      )}
      {!!blogPostsData && (
        <>
          <RecipesWrapper>
            <HerbsAndSpicesBgElement
              src={"/assets/img/styling/herbs-and-spices-2.webp"}
              alt={""}
              initial={{
                scale: hideAnimation ? 1 : 2,
                x: hideAnimation ? 0 : "100%",
                rotate: hideAnimation ? 15 : 75,
              }}
              animate={{
                scale: hideAnimation ? 1 : inView ? 1 : 2,
                x: hideAnimation ? 0 : inView ? 0 : "100%",
                rotate: hideAnimation ? 15 : inView ? 15 : 75,
              }}
              transition={{
                ease: "easeIn",
                duration: 0.3,
              }}
            />
            <HerbBgElement src={"/assets/img/styling/herb-3.webp"} alt={""} />
            <StyledContainer>
              <RecipesTitleContainer>
                <img
                  src={"/assets/img/icons/knives-icon-light.webp"}
                  alt={""}
                />
                <BannerTitle dark={dark}>{t("read_our_articles")}</BannerTitle>
              </RecipesTitleContainer>
              <Row gutter={[16, 16]}>
                {blogPostsData.items.map((item, index) => (
                  <Col lg={12} key={index}>
                    <BlogPostItem post={item} />
                  </Col>
                ))}
              </Row>
              <PrimaryLinkButton
                style={{
                  width: "fit-content",
                  minWidth: 200,
                  marginInline: "auto",
                }}
                link={urls.blog}
                text={t("view_all_blog_posts")}
              />
            </StyledContainer>
          </RecipesWrapper>
        </>
      )}
      <SpiceSplashBgElement
        style={{ top: recipesData ? "50%" : "30%" }}
        hideAnimation={hideAnimation}
      />
      {dark ? (
        <>
          <RedSpiceBottomBgElement
            src={"/assets/img/styling/spices-6.png"}
            alt={""}
            positionRight={data?.sections?.length % 2 !== 0}
          />
          <YellowSpiceBottomBgElement
            src={"/assets/img/styling/spice-splash-yellow.webp"}
            alt={""}
          />
        </>
      ) : (
        <HerbsAndSpicesBgElementLight
          src={"/assets/img/styling/herbs-and-spices-4.webp"}
          alt={""}
        />
      )}
      {data?.sections?.map((section, index) => (
        <article className={"container pt-5 pb-5"} key={index}>
          <Row gutter={[32, 16]}>
            {!!section.image && (
              <Col
                lg={14}
                style={{ order: isMobile ? 0 : index % 2 === 0 ? 1 : 0 }}
              >
                <img
                  src={section.image}
                  alt={section.image_alt}
                  style={{
                    width: "100%",
                    borderRadius: 4,
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                  }}
                />
              </Col>
            )}
            <Col
              lg={10}
              style={{ order: isMobile ? 1 : index % 2 === 0 ? 0 : 1 }}
            >
              <BannerTitle
                dark={dark}
                style={{
                  textAlign: "start",
                  color: dark ? "#fff" : colors.primaryDark,
                }}
              >
                {section.title}
              </BannerTitle>
              <ReactMarkdown>{section.description}</ReactMarkdown>
              {!!section.button_link && (
                <PrimaryLinkButton
                  style={{ width: "fit-content", minWidth: 200 }}
                  link={section.button_link}
                  text={section.button_text}
                />
              )}
            </Col>
          </Row>
        </article>
      ))}
    </Wrapper>
  );
}

const SpiceSplashBgElement = ({ style, hideAnimation }) => {
  const { ref, inView } = useInView({
    rootMargin: "-50% 0% -50% 0%",
    triggerOnce: true,
  });

  return (
    <SpiceSplashBgElementWrapper style={{ ...style }} ref={ref}>
      <RedSpiceRightBgElement
        src={"/assets/img/styling/spices-6.png"}
        alt={""}
        style={{ rotate: -90 }}
        initial={{
          scale: hideAnimation ? 1 : 0.8,
          x: hideAnimation ? 0 : "30%",
        }}
        animate={{
          scale: hideAnimation ? 1 : inView ? 1 : 0.8,
          x: hideAnimation ? 0 : inView ? 0 : "30%",
        }}
        transition={{
          ease: "easeIn",
          duration: 0.25,
        }}
      />
      <YellowSpiceBgElement
        src={"/assets/img/styling/spice-splash-yellow.webp"}
        alt={""}
        initial={{
          scale: hideAnimation ? 1 : 0,
          opacity: hideAnimation ? 0.4 : 1,
        }}
        animate={{
          scale: hideAnimation ? 1 : inView ? 1 : 0,
          opacity: hideAnimation ? 0.4 : inView ? 0.4 : 1,
        }}
        transition={{
          ease: "easeIn",
          duration: 0.25,
        }}
      />
    </SpiceSplashBgElementWrapper>
  );
};

const Wrapper = styled.section`
  background: ${({ dark }) =>
    dark
      ? '#000000 url("/assets/img/bg/background-dark-1.webp") no-repeat center center / cover'
      : "initial"};
  margin-block: ${styles.banner_padding_mobile};
  overflow: hidden;
  overflow: clip;
  position: relative;
  isolation: isolate;

  p,
  ul {
    color: ${({ dark }) => (dark ? "white" : "initial")};
    font-size: 16px;
    font-weight: 300;
    margin: 0;
  }

  h3 {
    color: ${({ dark }) => (dark ? "white" : "initial")};
  }

  ul {
    list-style: disc;
    margin-left: 16px;
  }

  .ant-col {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.5rem;
  }

  @media ${device.lg} {
    padding-bottom: ${styles.banner_padding_mobile};
  }
`;

const RecipesWrapper = styled.section`
  padding-block: 3rem;

  position: relative;
`;

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const RecipesTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;

  img {
    width: 56px;

    @media ${device.md} {
      width: 52px;
    }
  }
`;

const HerbsAndSpicesBgElement = styled(BackgroundElement)`
  display: none;

  @media ${device.sm} {
    width: 300px;
    top: 30px;
    right: -120px;
    display: block;
  }

  @media ${device.lg} {
    width: 360px;
    top: 30px;
    right: -150px;
  }
`;

const HerbBgElement = styled(BackgroundElement)`
  display: none;

  @media ${device.sm} {
    transform: rotate(-15deg);
    width: 200px;
    bottom: -50px;
    left: -100px;
    display: block;
  }

  @media ${device.md} {
    width: 220px;
  }

  @media ${device.lg} {
    bottom: -60px;
    left: -110px;
  }

  @media ${device.xl} {
    bottom: -90px;
    left: -110px;
  }
`;

const RedSpiceBgElement = styled(BackgroundElement)`
  width: 250px;
`;

const RedSpiceRightBgElement = styled(RedSpiceBgElement)`
  display: none;
  transform: rotate(-90deg);
  top: 40%;
  right: -90px;

  @media ${device.sm} {
    display: block;
  }

  @media ${device.lg} {
    right: -100px;
  }
`;

const YellowSpiceBgElement = styled(BackgroundElement)`
  opacity: 0.4;
  top: -30%;
  right: -590px;
  z-index: -2;

  @media ${device.xl} {
    right: -580px;
  }
`;

const SpiceSplashBgElementWrapper = styled.div`
  position: absolute;
  height: 500px;
  right: 0;
`;

const RedSpiceBottomBgElement = styled(RedSpiceBgElement)`
  display: none;

  @media ${device.lg} {
    display: block;
    bottom: -16px;
    left: 20px;
  }
`;

const YellowSpiceBottomBgElement = styled(YellowSpiceBgElement)`
  display: none;
  top: unset;
  right: unset;
  bottom: -30%;
  left: -240px;

  @media ${device.lg} {
    display: block;
  }
`;

const HerbsAndSpicesBgElementLight = styled(BackgroundElement)`
  transform: rotate(240deg);
  width: 240px;
  top: -10px;
  left: -120px;

  @media ${device.sm} {
    display: block;
    left: -90px;
  }

  @media ${device.md} {
    width: 240px;
  }

  @media ${device.lg} {
    top: unset;
    bottom: 0;
    transform: rotate(140deg);

    width: 280px;
    left: -100px;
  }

  @media ${device.xxl} {
    transform: rotate(240deg);

    top: 0;
    width: min(17vw, 320px);
    left: -80px;
  }
`;
